import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AppState, Platform, type AppStateStatus } from 'react-native';
import { useAuthStore } from '../../auth/store/useAuthStore';
import { canRunOperationalBackgroundWork } from '../session/accountSetupService';
import { getSessionOrganizationId } from '../session-api';
import { useSessionStore } from '../session/store/useSessionStore';
import { getHomeSnapshot } from '../home/homeSnapshotRepo';
import { captureSentryError } from '../../shared/sentry/sentryUser';
import { ensureFreshAccessToken, supabase } from '../../shared/supabase/supabaseClient'
import { isSupabaseJwtExpiredMessage } from '../../shared/supabase/supabaseAuthSession';
import { usePublicStoreFeaturesVisible } from '../feature-flags/usePublicStoreFeaturesVisible';
import { useHasBusinessCapability } from '../session/hooks/useEffectiveBusinessCapabilities';

type PendingOrdersCountContextValue = {
  /** Pedidos status=pending de la sucursal actual (Home / segmento Pedidos). */
  count: number | null;
  pendingOrders: number | null;
  openComplaints: number | null;
  unreadMessages: number | null;
  unreadConsultations: number | null;
  unreadQuotes: number | null;
  /** Suma para badge Pedidos en sidebar/tab (a dónde ir en el inbox). */
  inboxBadge: number | null;
  /** force: saltea el TTL del snapshot (eventos push, writes propios). */
  refresh: (opts?: { force?: boolean }) => Promise<void>;
};

const PendingOrdersCountContext = createContext<PendingOrdersCountContextValue | null>(null);

function sumCounts(...parts: Array<number | null>): number | null {
  if (parts.every((p) => p == null)) return null;
  return parts.reduce<number>((acc, p) => acc + (p ?? 0), 0);
}

/**
 * Sesión muerta genera ruido en badges: JWT expired explícito, o 401 con body
 * vacío (supabase devuelve error sin message/code → repos throw `new Error('')`
 * o `new Error('countX failed (code unknown)')`). El auth retry ya intentó
 * refrescar y el auth layer maneja logout — no es bug, es degradación esperada.
 */
function isBadgeAuthNoise(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const msg = error.message ?? '';
  if (isSupabaseJwtExpiredMessage(msg)) return true;
  return msg === '' || /failed \(code unknown\)/.test(msg);
}

export function PendingOrdersCountProvider({ children }: { children: React.ReactNode }) {
  const userId = useAuthStore((s) => s.session?.user?.id);
  const branchId = useSessionStore((s) => s.currentBranchId);
  const publicStoreOn = usePublicStoreFeaturesVisible();
  const hasServiceQuotes = useHasBusinessCapability('service_quotes');
  const [pendingOrders, setPendingOrders] = useState<number | null>(null);
  const [openComplaints, setOpenComplaints] = useState<number | null>(null);
  const [unreadMessages, setUnreadMessages] = useState<number | null>(null);
  const [unreadConsultations, setUnreadConsultations] = useState<number | null>(null);
  const [unreadQuotes, setUnreadQuotes] = useState<number | null>(null);

  const resetCounts = useCallback(() => {
    setPendingOrders(null);
    setOpenComplaints(null);
    setUnreadMessages(null);
    setUnreadConsultations(null);
    setUnreadQuotes(null);
  }, []);

  const refresh = useCallback(async (opts?: { force?: boolean }) => {
    if (!userId || !canRunOperationalBackgroundWork() || !publicStoreOn) {
      resetCounts();
      return;
    }
    try {
      // Pre-validar token antes de disparar la RPC.
      // Si el refresh token está muerto, ensureFreshAccessToken retorna null
      // (y refreshSupabaseSessionAndSyncStore ya disparó logout('session_invalid')).
      // Si es un blip de red, también retorna null pero sin logout.
      // En ambos casos: no disparar la llamada, badges a null.
      const token = await ensureFreshAccessToken();
      if (!token) {
        resetCounts();
        return;
      }

      if (!branchId || !(await getSessionOrganizationId())) {
        resetCounts();
        return;
      }
      // Una RPC cubre los 5 badges (pedidos branch + org + soporte user) y
      // comparte cache con Home/evidencias: refrescos concurrentes dedupean.
      const snap = await getHomeSnapshot(branchId, { force: opts?.force });
      const badges = snap?.badges ?? null;
      setPendingOrders(badges?.ordersPending ?? null);
      setOpenComplaints(badges?.complaintsOpen ?? null);
      setUnreadMessages(badges?.orderUnreadConversations ?? null);
      setUnreadConsultations(badges?.consultationsUnread ?? null);
      setUnreadQuotes(hasServiceQuotes ? badges?.quotesUnread ?? null : 0);
    } catch (error) {
      if (!isBadgeAuthNoise(error)) {
        captureSentryError(error, { tags: { module: 'shell', op: 'pending-count-snapshot' } });
      }
      resetCounts();
    }
  }, [branchId, hasServiceQuotes, publicStoreOn, resetCounts, userId]);

  useEffect(() => {
    if (userId && canRunOperationalBackgroundWork() && publicStoreOn) {
      void refresh();
    } else {
      resetCounts();
    }
  }, [publicStoreOn, refresh, resetCounts, userId]);
  const visibleRef = useRef(true);
  useEffect(() => {
    const onActive = () => {
      if (canRunOperationalBackgroundWork() && publicStoreOn) void refresh();
    };
    const sub = AppState.addEventListener('change', (next: AppStateStatus) => {
      visibleRef.current = next === 'active';
      if (next === 'active') onActive();
    });
    let removeVis: (() => void) | undefined;
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      const onVis = () => {
        visibleRef.current = document.visibilityState === 'visible';
        if (visibleRef.current) onActive();
      };
      document.addEventListener('visibilitychange', onVis);
      visibleRef.current = document.visibilityState === 'visible';
      removeVis = () => document.removeEventListener('visibilitychange', onVis);
    }
    const interval = setInterval(() => {
      if (visibleRef.current) onActive();
    }, 60_000);
    return () => {
      sub.remove();
      removeVis?.();
      clearInterval(interval);
    };
  }, [publicStoreOn, refresh]);

  // Realtime always-on en customer_orders de la sucursal: un INSERT/UPDATE
  // dispara refresh con force (evento real → saltea el TTL de 15s del snapshot).
  // El interval de 60s de arriba queda como fallback si el canal cae.
  const realtimeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!userId || !branchId || !publicStoreOn || !canRunOperationalBackgroundWork()) return;

    const scheduleRefresh = () => {
      if (!visibleRef.current) return;
      if (realtimeTimerRef.current) clearTimeout(realtimeTimerRef.current);
      realtimeTimerRef.current = setTimeout(() => {
        void refresh({ force: true });
      }, 500);
    };

    let channel: ReturnType<typeof supabase.channel> | null = null;
    let cancelled = false;
    const branch = branchId.trim();

    void (async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token || cancelled) return;
      await supabase.realtime.setAuth(session.access_token);
      if (cancelled) return;

      channel = supabase
        .channel(`orders-badge-realtime-${branch}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'customer_orders',
            filter: `branch_id=eq.${branch}`,
          },
          scheduleRefresh,
        )
        .subscribe((status) => {
          if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            scheduleRefresh();
          }
        });
    })();

    return () => {
      cancelled = true;
      if (realtimeTimerRef.current) clearTimeout(realtimeTimerRef.current);
      if (channel) void supabase.removeChannel(channel);
    };
  }, [userId, branchId, publicStoreOn, refresh]);

  const inboxBadge = useMemo(
    () => sumCounts(pendingOrders, openComplaints, unreadMessages, unreadConsultations, unreadQuotes),
    [openComplaints, pendingOrders, unreadConsultations, unreadMessages, unreadQuotes],
  );

  const value = useMemo(
    () => ({
      count: pendingOrders,
      pendingOrders,
      openComplaints,
      unreadMessages,
      unreadConsultations,
      unreadQuotes,
      inboxBadge,
      refresh,
    }),
    [inboxBadge, openComplaints, pendingOrders, refresh, unreadConsultations, unreadMessages, unreadQuotes],
  );

  return (
    <PendingOrdersCountContext.Provider value={value}>{children}</PendingOrdersCountContext.Provider>
  );
}

export function usePendingOrdersCount(): PendingOrdersCountContextValue {
  const ctx = useContext(PendingOrdersCountContext);
  if (!ctx) {
    return {
      count: null,
      pendingOrders: null,
      openComplaints: null,
      unreadMessages: null,
      unreadConsultations: null,
      unreadQuotes: null,
      inboxBadge: null,
      refresh: async () => undefined,
    };
  }
  return ctx;
}
